import { URLS, queryClient } from '@/data';
import { ripsKeys } from '@/providers';
import { logger } from '@/utils/logging';
import { useQuery } from '@tanstack/react-query';

import { useRipsSession } from './use-rips-session';

const LOG_TAG = '[useRipsInventoryCount]';

type InventoryResponse = {
    meta: {
        /** Revealed cards the player owns across all pages. */
        total_count: number;
    };
};

// Only the total is needed, so ask for the smallest page.
const INVENTORY_PATH = '/loot_machine/api/game_platform/iframe/v1/inventory?page=1&per=1';

const fetchInventory = async (sessionToken: string): Promise<InventoryResponse> => {
    const response = await fetch(`${URLS.REPACKZ_API_URL}${INVENTORY_PATH}`, {
        headers: { 'X-Platform-Session': sessionToken },
    });

    if (!response.ok) {
        logger.warn(LOG_TAG, `HTTP error! status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};

/** Number of cards in the player's Repackz inventory, shown as the Vault tab badge. */
export const useRipsInventoryCount = () => {
    const { sessionToken } = useRipsSession();

    const { data } = useQuery({
        // Keyed by token, so a refreshed session refetches the count.
        queryKey: [...ripsKeys.inventoryCount(), sessionToken],
        queryFn: () => (sessionToken ? fetchInventory(sessionToken) : null),
        select: response => response?.meta.total_count ?? 0,
        enabled: Boolean(sessionToken),
        staleTime: Infinity,
    });

    return data ?? 0;
};

/** Refetches the count, e.g. when the player is back from the Rips web app where cards are revealed or sold. */
export const refreshRipsInventoryCount = () => queryClient.invalidateQueries({ queryKey: ripsKeys.inventoryCount() });
